import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import AddIcon from "@mui/icons-material/Add";
import { LEAD_SOURCE_LABELS } from "@/types/enums";
import type { LeadSource } from "@/types/enums";
import { PageHeader } from "@/components/common/PageHeader";
import { QueryState } from "@/components/common/AsyncState";
import { KpiCard } from "@/components/common/KpiCard";
import { useAuth } from "@/auth/useAuth";
import { useBranch } from "@/auth/BranchContext";
import { fetchBranches } from "@/api/branches";
import {
  createRegistration,
  enrollRegistration,
  fetchRegistrations,
  markRegistrationLeft,
  toggleRegistrationLesson,
  toggleRegistrationTrial,
  updateRegistration,
  type RegistrationInput,
  type RegistrationRow,
} from "@/api/registrations";
import { formatDate } from "@/utils/format";

function progressPercent(value: number, target: number): number {
  return target > 0 ? Math.min(100, Math.round((value / target) * 100)) : 0;
}

export function RegistrationPage() {
  const { hasPermission } = useAuth();
  const { effectiveBranchId } = useBranch();
  const canEdit = hasPermission("leads.edit");
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [subjectFilter, setSubjectFilter] = useState("");
  const [groupFilter, setGroupFilter] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<RegistrationRow | null>(null);

  const { data: branches } = useQuery({ queryKey: ["branches"], queryFn: fetchBranches });
  const { data, isLoading, error } = useQuery({
    queryKey: ["registrations", effectiveBranchId, search],
    queryFn: () => fetchRegistrations({ branchId: effectiveBranchId, search: search || undefined, limit: 200 }),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ["registrations"] });

  const toggleTrial = useMutation({ mutationFn: toggleRegistrationTrial, onSuccess: invalidate });
  const toggleLesson = useMutation({ mutationFn: toggleRegistrationLesson, onSuccess: invalidate });
  const markLeft = useMutation({ mutationFn: markRegistrationLeft, onSuccess: invalidate });
  const enroll = useMutation({ mutationFn: (id: string) => enrollRegistration(id), onSuccess: invalidate });

  const kpi = data?.kpi;
  const allRows = useMemo(() => data?.items ?? [], [data]);

  const subjectOptions = useMemo(
    () => Array.from(new Set(allRows.map((r) => r.subjectName).filter(Boolean))).sort(),
    [allRows],
  );
  const groupOptions = useMemo(
    () => Array.from(new Set(allRows.map((r) => r.groupCode).filter(Boolean) as string[])).sort(),
    [allRows],
  );

  const rows = useMemo(
    () =>
      allRows.filter((row) => {
        if (sourceFilter && row.source !== sourceFilter) return false;
        if (subjectFilter && row.subjectName !== subjectFilter) return false;
        if (groupFilter && row.groupCode !== groupFilter) return false;
        if (stageFilter === "wait" && (row.lessonAttended || row.left)) return false;
        if (stageFilter === "trial" && !row.trialAttended) return false;
        if (stageFilter === "lesson" && !row.lessonAttended) return false;
        if (stageFilter === "left" && !row.left) return false;
        if (stageFilter === "nogroup" && row.groupCode) return false;
        return true;
      }),
    [allRows, sourceFilter, subjectFilter, groupFilter, stageFilter],
  );

  const filtersApplied = Boolean(search || sourceFilter || subjectFilter || groupFilter || stageFilter);

  return (
    <>
      <PageHeader
        title="Регистрация"
        subtitle="Воронка новых учеников: регистрация → пробный → первый урок"
        actions={canEdit && <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditing(null); setDialogOpen(true); }}>Новая регистрация</Button>}
      />
      <QueryState isLoading={isLoading} error={error}>
        {kpi && (
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={2}>
            <KpiCard label="Ожидание" value={String(kpi.waiting)} hint="записались, ждут пробный или первый урок" color="primary.main" />
            <KpiCard
              label="Регистраций за месяц"
              value={String(kpi.total)}
              hint={`цель ${kpi.targets.reg}`}
              progress={progressPercent(kpi.total, kpi.targets.reg)}
            />
            <KpiCard
              label="Дошли до пробного"
              value={String(kpi.trial)}
              hint={`цель ${kpi.targets.trial}`}
              progress={progressPercent(kpi.trial, kpi.targets.trial)}
            />
            <KpiCard
              label="Дошли до урока"
              value={String(kpi.lesson)}
              hint={`цель ${kpi.targets.lesson}`}
              progress={progressPercent(kpi.lesson, kpi.targets.lesson)}
            />
          </Stack>
        )}
        <Paper className="crm-data-panel" variant="outlined">
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} p={2} alignItems={{ md: "center" }} flexWrap="wrap" useFlexGap>
            <TextField
              size="small"
              placeholder="Поиск по имени или телефону…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 240 }}
            />
            <TextField select size="small" label="Источник" value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} sx={{ minWidth: 165 }}>
              <MenuItem value="">Все источники</MenuItem>
              {(Object.keys(LEAD_SOURCE_LABELS) as LeadSource[]).map((key) => (
                <MenuItem key={key} value={key}>
                  {LEAD_SOURCE_LABELS[key]}
                </MenuItem>
              ))}
            </TextField>
            <TextField select size="small" label="Предмет" value={subjectFilter} onChange={(e) => setSubjectFilter(e.target.value)} sx={{ minWidth: 165 }}>
              <MenuItem value="">Все предметы</MenuItem>
              {subjectOptions.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </TextField>
            <TextField select size="small" label="Группа" value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)} sx={{ minWidth: 150 }}>
              <MenuItem value="">Все группы</MenuItem>
              {groupOptions.map((code) => (
                <MenuItem key={code} value={code}>
                  {code}
                </MenuItem>
              ))}
            </TextField>
            <TextField select size="small" label="Этап" value={stageFilter} onChange={(e) => setStageFilter(e.target.value)} sx={{ minWidth: 175 }}>
              <MenuItem value="">Любой этап</MenuItem>
              <MenuItem value="wait">Ожидание</MenuItem>
              <MenuItem value="trial">Был на пробном</MenuItem>
              <MenuItem value="lesson">Дошёл до урока</MenuItem>
              <MenuItem value="left">Ушёл</MenuItem>
              <MenuItem value="nogroup">Без группы</MenuItem>
            </TextField>
            {filtersApplied && (
              <Button
                size="small"
                onClick={() => {
                  setSearch("");
                  setSourceFilter("");
                  setSubjectFilter("");
                  setGroupFilter("");
                  setStageFilter("");
                }}
              >
                Сбросить
              </Button>
            )}
            <Box flexGrow={1} />
            <Typography variant="caption" color="text.secondary">
              найдено: <b>{rows.length}</b> из {allRows.length}
            </Typography>
          </Stack>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Дата</TableCell>
                <TableCell>Ф.И.О.</TableCell>
                <TableCell>Телефон</TableCell>
                <TableCell>Группа</TableCell>
                <TableCell>Предмет</TableCell>
                <TableCell>Пробный</TableCell>
                <TableCell>Урок</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} sx={{ opacity: row.left ? 0.55 : 1 }}>
                  <TableCell>{formatDate(row.date)}</TableCell>
                  <TableCell><b>{row.fullName}</b>{row.left && <Chip size="small" label="ушёл" sx={{ ml: 1 }} />}</TableCell>
                  <TableCell>{row.phone}</TableCell>
                  <TableCell>{row.groupCode ?? "—"}</TableCell>
                  <TableCell>{row.subjectName}</TableCell>
                  <TableCell>
                    {canEdit && !row.left && (
                      <Chip clickable label={row.trialAttended ? "Пришёл ✓" : "—"} color={row.trialAttended ? "success" : "default"} onClick={() => toggleTrial.mutate(row.id)} />
                    )}
                  </TableCell>
                  <TableCell>
                    {canEdit && !row.left && (
                      <Chip clickable label={row.lessonAttended ? "Пришёл ✓" : "—"} color={row.lessonAttended ? "success" : "default"} onClick={() => toggleLesson.mutate(row.id)} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      {canEdit && !row.left && !row.enrolled && <Button size="small" variant="contained" onClick={() => enroll.mutate(row.id)}>Зачислить</Button>}
                      {row.enrolled && <Chip size="small" color="success" label="зачислен" />}
                      {canEdit && <Button size="small" onClick={() => { setEditing(row); setDialogOpen(true); }}>Изменить</Button>}
                      {canEdit && !row.left && <Button size="small" color="warning" onClick={() => markLeft.mutate(row.id)}>Ушёл</Button>}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </QueryState>
      <RegistrationDialog
        open={dialogOpen}
        branches={branches ?? []}
        initial={editing}
        defaultBranchId={effectiveBranchId ?? branches?.[0]?.id ?? ""}
        onClose={() => setDialogOpen(false)}
        onSaved={invalidate}
      />
    </>
  );
}

function RegistrationDialog({
  open,
  onClose,
  onSaved,
  branches,
  initial,
  defaultBranchId,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  branches: { id: string; name: string }[];
  initial: RegistrationRow | null;
  defaultBranchId: string;
}) {
  const [form, setForm] = useState<RegistrationInput>(() => ({
    branchId: defaultBranchId,
    date: new Date().toISOString().slice(0, 10),
    fullName: "",
    phone: "",
    subjectName: "",
  }));

  const save = useMutation({
    mutationFn: async () => {
      if (initial) return updateRegistration(initial.id, form);
      return createRegistration(form);
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{initial ? "Редактировать регистрацию" : "Новая регистрация"}</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} mt={1}>
          <TextField select label="Филиал" value={form.branchId} onChange={(e) => setForm({ ...form, branchId: e.target.value })}>
            {branches.map((b) => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
          </TextField>
          <TextField label="Дата" type="date" InputLabelProps={{ shrink: true }} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          <TextField label="Ф.И.О." value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} />
          <TextField label="Телефон" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <TextField label="Возраст" type="number" value={form.age ?? ""} onChange={(e) => setForm({ ...form, age: Number(e.target.value) || undefined })} />
          <TextField label="Код группы" value={form.groupCode ?? ""} onChange={(e) => setForm({ ...form, groupCode: e.target.value })} />
          <TextField label="Предмет" value={form.subjectName} onChange={(e) => setForm({ ...form, subjectName: e.target.value })} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Отмена</Button>
        <Button variant="contained" onClick={() => save.mutate()} disabled={save.isPending}>Сохранить</Button>
      </DialogActions>
    </Dialog>
  );
}
